import { z } from 'zod';
import { FrontendSpecificConfig, shared } from '@tig-dpqa-cloud/contract-backend-common';
import { Permissions } from '../permissions';

export const UserConfigSchema = z.object({
  permissions: z.array(Permissions.exclude([Permissions.enum.DEFAULT])),
  firstName: z.string(),
  lastName: z.string(),
  email: z.email(),
  eulaAcceptedAt: z.iso.datetime({ offset: true }).optional().describe('When the user accepted the EULA'),
});

export type UserConfigType = z.infer<typeof UserConfigSchema>;
export type UserPermissionsType = UserConfigType['permissions'];

export const UserContextGetResponseSchema = z
  .object({
    user: UserConfigSchema,
    tenant: FrontendSpecificConfig.optional(),
    possibleTenants: z.array(z.object({
      id: shared.TenantId,
      displayName: z.string().optional(),
    })).prefault([]),
    isTenantOnboarded: z.literal(true).optional(),
    dataplaneVersion: z
      .array(
        z.object({
          id: z.uuid(),
          lastPingedAt: z
            .date()
            .transform((date) => date.toISOString())
            .optional(),
          version: z.string().describe('The dataplane version').optional(),
        }),
      )
      .optional(),
    couldUpgradeDataplane: z.boolean().optional(),
    latestDataplaneTag: z.string().optional(),
    credentials: shared.AWSRumCredentials.optional(),
    eula: z.object({
      url: z.url().describe('Presigned URL to download the EULA pdf'),
      uploadedAt: z.date().transform((date) => date.toISOString()).describe('When the EULA PDF was uploaded'),
    }),
    ifu: z.object({
      url: z.url().describe('Presigned URL to download the IFU pdf'),
    }),
  })
  .refine(
    // If the user has no permissions, the tenant config should be empty, This is mostly a sanity check
    ({ user, tenant, dataplaneVersion }) =>
      user.permissions.length !== 0 || (tenant && Object.keys(tenant).length !== 0 && dataplaneVersion),
    {
      error: 'User without permissions cannot access the tenant configuration',
    },
  );

export type UserContextGetResponseType = z.infer<typeof UserContextGetResponseSchema>;
